// src/config/constants.js
// ✅ Track A — A1 (Mar 26, 2026): FEATURE_FLAGS + PLAN_DEFAULTS added
// ✅ A1 FIX (Mar 26, 2026): Added missing exports — getTemplateTypeLabel, getCourtTypeLabel,
//    GENDER_OPTIONS, ALLOWED_DRAFT_TYPES, FILE_LIMITS, formatFileSize
// ✅ BUG FIX (Apr 07, 2026): Unified case status system — constants.js now matches
//    CaseService.js and firestore.rules exactly.
// ✅ BUG FIX (Apr 09, 2026): Added missing TEMPLATE_TYPES — Appeal and Revision added.
// ✅ SPRINT-CASE-STAGE-001 (Jun 26, 2026): ADR-CASE-STAGE-001 v1.0 implementation.
//    - CASE_STATUS_LIST: rebuilt as clean 11-value list (retired Admitted, In Progress,
//      Hearing, Adjourned, Soft Close Pending — these were procedural stage values
//      that belong in CASE_STAGES, not lifecycle status).
//    - CASE_CLUSTERS: 7 practice area clusters
//    - CASE_CATEGORIES: 38 categories with slug, label, cluster, stageMap
//    - CASE_STAGES: 28 stage maps, STAGE_MAP_VERSION "1.0.0" — frozen under ADR-CASE-STAGE-001
//    - PARALLEL_STAGES: stages that run concurrently without advancing main track
//    - CATEGORY_TO_CLUSTER: lookup map for auto-deriving cluster from category
//    - CATEGORY_SPECIFIC_FIELDS: which categories require extra sub-fields
//    - DRAFT_CATEGORIES: aliased to CASE_CATEGORIES for migration (will be removed post B3)
// ─────────────────────────────────────────────────────────────────────────────

// ============================================
// CLOUD FUNCTIONS BASE URL  (v2.3.4, Oct 2026)
// Single source of truth for every raw fetch() call to an onRequest Cloud
// Function. Read from REACT_APP_CF_BASE.
// NO hardcoded fallback in ANY environment: a missing value must fail loudly,
// otherwise a build can silently call the wrong project's backend.
//   DEV : https://asia-south1-handyy-lpms-dev.cloudfunctions.net
//   PROD: https://asia-south1-lexcore-prod.cloudfunctions.net
// ============================================
const _cfBase = process.env.REACT_APP_CF_BASE;
if (!_cfBase) {
  throw new Error(
    "REACT_APP_CF_BASE is not set. Add it to client/.env (see env.prod.template) and restart the build."
  );
}
export const CF_BASE = _cfBase.replace(/\/+$/, "");

// ============================================
// STAGE MAP VERSION — FROZEN UNDER ADR-CASE-STAGE-001
// DO NOT INCREMENT without filing a new ADR.
// ============================================
export const STAGE_MAP_VERSION = '1.0.0';

// ============================================
// CASE CLUSTERS — 7 Practice Area Clusters
// ============================================
export const CASE_CLUSTERS = [
  { slug: 'civil_litigation',        label: 'Civil Litigation' },
  { slug: 'criminal',                label: 'Criminal Litigation' },
  { slug: 'family_personal',         label: 'Family & Personal Law' },
  { slug: 'commercial_corporate',    label: 'Commercial & Corporate Law' },
  { slug: 'service_matters',         label: 'Service Matters' },
  { slug: 'constitutional_writ',     label: 'Constitutional, Writ & Regulatory' },
  { slug: 'quasi_judicial_special',  label: 'Quasi-Judicial & Special Proceedings' },
];

// ============================================
// CASE CATEGORIES — 38 Categories
// Each category: { slug, label, cluster, stageMap }
// stageMap references a key in CASE_STAGES
// ============================================
export const CASE_CATEGORIES = [
  // ── Cluster 1: Civil Litigation ───────────────────────────────────────────
  { slug: 'civil',               label: 'Civil / Suit',                        cluster: 'civil_litigation',       stageMap: 'civil_stages' },
  { slug: 'money_recovery',      label: 'Money Recovery Suit',                 cluster: 'civil_litigation',       stageMap: 'money_recovery_stages' },
  { slug: 'injunction',          label: 'Injunction / Specific Performance',   cluster: 'civil_litigation',       stageMap: 'civil_stages' },
  { slug: 'execution',           label: 'Execution (Standalone)',               cluster: 'civil_litigation',       stageMap: 'execution_stages' },
  { slug: 'rent_eviction',       label: 'Rent / Eviction',                     cluster: 'civil_litigation',       stageMap: 'civil_stages' },
  { slug: 'land_acquisition',    label: 'Land Acquisition',                    cluster: 'civil_litigation',       stageMap: 'land_acquisition_stages' },
  { slug: 'revenue_agency',      label: 'Revenue / Agency Laws',               cluster: 'civil_litigation',       stageMap: 'revenue_stages' },

  // ── Cluster 2: Criminal Litigation ────────────────────────────────────────
  { slug: 'criminal',            label: 'Criminal (BNS/BNSS)',                 cluster: 'criminal',               stageMap: 'criminal_stages' },
  { slug: 'ni_act',              label: 'NI Act / Cheque Bounce',              cluster: 'criminal',               stageMap: 'ni_act_stages' },
  { slug: 'bail',                label: 'Bail Application',                    cluster: 'criminal',               stageMap: 'bail_stages' },
  { slug: 'pocso',               label: 'POCSO / Juvenile',                    cluster: 'criminal',               stageMap: 'pocso_stages' },

  // ── Cluster 3: Family & Personal Law ──────────────────────────────────────
  { slug: 'family',              label: 'Family / Divorce (HMA)',              cluster: 'family_personal',        stageMap: 'family_stages' },
  { slug: 'maintenance',         label: 'Maintenance / Alimony',               cluster: 'family_personal',        stageMap: 'maintenance_stages' },
  { slug: 'guardianship',        label: 'Guardianship & Wards',                cluster: 'family_personal',        stageMap: 'guardianship_stages' },
  { slug: 'domestic_violence',   label: 'Domestic Violence',                   cluster: 'family_personal',        stageMap: 'dv_stages' },
  { slug: 'muslim_personal_law', label: 'Muslim Personal Law',                 cluster: 'family_personal',        stageMap: 'muslim_stages' },
  { slug: 'succession',          label: 'Succession / Probate',                cluster: 'family_personal',        stageMap: 'succession_stages' },

  // ── Cluster 4: Commercial & Corporate Law ─────────────────────────────────
  { slug: 'arbitration',         label: 'Arbitration',                         cluster: 'commercial_corporate',   stageMap: 'arbitration_stages' },
  { slug: 'company',             label: 'Company / NCLT',                      cluster: 'commercial_corporate',   stageMap: 'company_stages' },
  { slug: 'insolvency',          label: 'Insolvency / IBC',                    cluster: 'commercial_corporate',   stageMap: 'insolvency_stages' },
  { slug: 'sarfaesi',            label: 'SARFAESI / DRT',                      cluster: 'commercial_corporate',   stageMap: 'sarfaesi_stages' },
  { slug: 'ip',                  label: 'Intellectual Property',               cluster: 'commercial_corporate',   stageMap: 'ip_stages' },

  // ── Cluster 5: Service Matters ────────────────────────────────────────────
  { slug: 'service_cat',              label: 'Service — CAT (Central)',          cluster: 'service_matters',        stageMap: 'service_stages' },
  { slug: 'service_state_tribunal',   label: 'Service — State Tribunal',         cluster: 'service_matters',        stageMap: 'service_stages' },
  { slug: 'service_hc',               label: 'Service — HC Writ',                cluster: 'service_matters',        stageMap: 'writ_stages' },
  { slug: 'service_sc',               label: 'Service — SC SLP',                 cluster: 'service_matters',        stageMap: 'writ_stages' },
  { slug: 'armed_forces',             label: 'Armed Forces Tribunal',             cluster: 'service_matters',        stageMap: 'armed_forces_stages' },
  { slug: 'psc_recruitment',          label: 'PSC / Recruitment / Election',      cluster: 'service_matters',        stageMap: 'writ_stages' },

  // ── Cluster 6: Constitutional, Writ & Regulatory ──────────────────────────
  { slug: 'writ',                label: 'Writ / PIL',                          cluster: 'constitutional_writ',    stageMap: 'writ_stages' },
  { slug: 'consumer',            label: 'Consumer',                            cluster: 'constitutional_writ',    stageMap: 'consumer_stages' },
  { slug: 'labour',              label: 'Labour / Industrial',                 cluster: 'constitutional_writ',    stageMap: 'labour_stages' },
  { slug: 'tax',                 label: 'Tax / Revenue Appeals',               cluster: 'constitutional_writ',    stageMap: 'tax_stages' },
  { slug: 'rera',                label: 'Real Estate / RERA',                  cluster: 'constitutional_writ',    stageMap: 'rera_stages' },
  { slug: 'ngt',                 label: 'NGT / Environment',                   cluster: 'constitutional_writ',    stageMap: 'ngt_stages' },

  // ── Cluster 7: Quasi-Judicial & Special Proceedings ───────────────────────
  { slug: 'motor_accident',      label: 'Motor Accident (MACT)',               cluster: 'quasi_judicial_special', stageMap: 'mact_stages' },
  { slug: 'quasi_judicial',      label: 'Quasi-Judicial Proceedings',          cluster: 'quasi_judicial_special', stageMap: 'quasi_judicial_stages' },
  { slug: 'lok_adalat',          label: 'Lok Adalat / Mediation',              cluster: 'quasi_judicial_special', stageMap: 'lok_adalat_stages' },
  { slug: 'notarial',            label: 'Notarial / Registration Matters',     cluster: 'quasi_judicial_special', stageMap: 'notarial_stages' },
  { slug: 'miscellaneous',       label: 'Miscellaneous / Other',               cluster: 'quasi_judicial_special', stageMap: null },
];

// ── Lookup: category slug → cluster slug (auto-derived, never user-entered) ──
export const CATEGORY_TO_CLUSTER = CASE_CATEGORIES.reduce((acc, cat) => {
  acc[cat.slug] = cat.cluster;
  return acc;
}, {});

// ── Lookup: category slug → stage map key ────────────────────────────────────
export const CATEGORY_TO_STAGE_MAP = CASE_CATEGORIES.reduce((acc, cat) => {
  acc[cat.slug] = cat.stageMap;
  return acc;
}, {});

// ── Stages that run in parallel (do not advance main track stage) ─────────────
// isParallel: true is set on stageHistory entries for these.
export const PARALLEL_STAGES = ['ia_interim_relief', 'adr_s89_referral'];

// ── Categories that require additional sub-fields on case create ──────────────
export const CATEGORY_SPECIFIC_FIELDS = {
  service_state_tribunal: ['stateName'],   // which state's tribunal
  quasi_judicial:         ['quasiJudicialForum'], // national forum datalist
};

// ============================================
// CASE STAGES — 28 Stage Maps
// Version: STAGE_MAP_VERSION "1.0.0"
// FROZEN under ADR-CASE-STAGE-001.
// DO NOT EDIT without a new ADR.
// Each stage: { value, label, isParallel? }
// ============================================
export const CASE_STAGES = {

  // ─── civil_stages ── used by: civil, injunction, rent_eviction ───────────
  // Chairman-corrected: cross-examination merged into evidence phases.
  // 3 new stages: ia_interim_relief (parallel), adr_s89_referral (parallel),
  // admission_denial.
  civil_stages: [
    { value: 'summons_issued',      label: 'Summons Issued' },
    { value: 'ia_interim_relief',   label: 'IA / Interim Relief (O.39/O.38)', isParallel: true },
    { value: 'appearance',          label: 'Appearance' },
    { value: 'written_statement',   label: 'Written Statement' },
    { value: 'replication',         label: 'Replication' },
    { value: 'adr_s89_referral',    label: 'ADR Referral (S.89 CPC)', isParallel: true },
    { value: 'admission_denial',    label: 'Admission & Denial / Markings' },
    { value: 'issues_framed',       label: 'Issues Framed' },
    { value: 'plaintiff_evidence',  label: 'Plaintiff Evidence (incl. cross)' },
    { value: 'defendant_evidence',  label: 'Defendant Evidence (incl. cross)' },
    { value: 'arguments_judgment',  label: 'Arguments & Judgment' },
    { value: 'decree',              label: 'Decree' },
    { value: 'execution',           label: 'Execution' },
  ],

  // ─── money_recovery_stages ── used by: money_recovery ───────────────────
  // O.XXXVII CPC — Summary Suit. Branch point at leave_order.
  // caseVariant: "leave_granted" / "leave_refused" set at leave_order stage.
  money_recovery_stages: [
    { value: 'plaint_filed',           label: 'Plaint Filed' },
    { value: 'summons_for_judgment',   label: 'Summons for Judgment' },
    { value: 'leave_application',      label: 'Leave to Defend Application' },
    { value: 'leave_order',            label: 'Leave Order' }, // → sets caseVariant
    { value: 'written_statement',      label: 'Written Statement (if leave granted)' },
    { value: 'evidence',               label: 'Evidence (if leave granted)' },
    { value: 'arguments',              label: 'Final Arguments' },
    { value: 'judgment',               label: 'Judgment' },
    { value: 'decree',                 label: 'Decree' },
    { value: 'execution',              label: 'Execution' },
  ],

  // ─── execution_stages ── used by: execution (standalone EP) ─────────────
  execution_stages: [
    { value: 'ep_filed',               label: 'EP Filed' },
    { value: 'notice_to_jd',           label: 'Notice to Judgment Debtor' },
    { value: 'attachment',             label: 'Attachment' },
    { value: 'sale_proclamation',      label: 'Sale Proclamation' },
    { value: 'auction_sale',           label: 'Auction / Sale' },
    { value: 'satisfaction',           label: 'Decree Satisfied' },
  ],

  // ─── land_acquisition_stages ── used by: land_acquisition ───────────────
  land_acquisition_stages: [
    { value: 'section_4_notification', label: 'S.4 Notification' },
    { value: 'objections_s11',         label: 'Objections — S.11 Inquiry' },
    { value: 'award_section_11',       label: 'Award (S.11)' },
    { value: 'reference_s18',          label: 'Reference S.18 to Court' },
    { value: 'court_hearing',          label: 'Court Hearing' },
    { value: 'enhanced_compensation',  label: 'Enhanced Compensation Order' },
    { value: 'execution',              label: 'Execution / Recovery' },
  ],

  // ─── revenue_stages ── used by: revenue_agency ───────────────────────────
  revenue_stages: [
    { value: 'application_filed',      label: 'Application Filed' },
    { value: 'notice',                 label: 'Notice Issued' },
    { value: 'hearing',                label: 'Hearing' },
    { value: 'order',                  label: 'Order' },
    { value: 'appeal_filed',           label: 'Appeal Filed' },
    { value: 'appeal_disposed',        label: 'Appeal Disposed' },
  ],

  // ─── criminal_stages ── used by: criminal ───────────────────────────────
  criminal_stages: [
    { value: 'fir_registered',         label: 'FIR Registered' },
    { value: 'chargesheet_filed',      label: 'Chargesheet Filed' },
    { value: 'cognizance_taken',       label: 'Cognizance Taken' },
    { value: 'bail_hearing',           label: 'Bail Hearing' },
    { value: 'charges_framed',         label: 'Charges Framed' },
    { value: 'prosecution_evidence',   label: 'Prosecution Evidence' },
    { value: 'defence_evidence',       label: 'Defence Evidence' },
    { value: 'final_arguments',        label: 'Final Arguments' },
    { value: 'judgment',               label: 'Judgment' },
  ],

  // ─── ni_act_stages ── used by: ni_act ───────────────────────────────────
  // Ends at judgment. Post-conviction execution = separate linked case.
  ni_act_stages: [
    { value: 'legal_notice',           label: 'Legal Notice Sent' },
    { value: 'complaint_filed',        label: 'Complaint Filed' },
    { value: 'summoning_order',        label: 'Summoning Order' },
    { value: 'accused_appearance',     label: 'Accused Appearance' },
    { value: 'plea_recording',         label: 'Plea Recording' },
    { value: 'complainant_evidence',   label: 'Complainant Evidence' },
    { value: 'defence_evidence',       label: 'Defence Evidence' },
    { value: 'final_arguments',        label: 'Final Arguments' },
    { value: 'judgment',               label: 'Judgment' },
  ],

  // ─── bail_stages ── used by: bail (standalone) ───────────────────────────
  bail_stages: [
    { value: 'application_filed',      label: 'Application Filed' },
    { value: 'notice_to_state',        label: 'Notice to State' },
    { value: 'hearing',                label: 'Hearing' },
    { value: 'order',                  label: 'Order' },
    { value: 'compliance',             label: 'Compliance / Surety' },
  ],

  // ─── pocso_stages ── used by: pocso ─────────────────────────────────────
  pocso_stages: [
    { value: 'fir_registered',         label: 'FIR Registered' },
    { value: 'special_court_assignment', label: 'Special Court Assignment' },
    { value: 'chargesheet_filed',      label: 'Chargesheet Filed' },
    { value: 'charges_framed',         label: 'Charges Framed' },
    { value: 'in_camera_trial',        label: 'In Camera Trial Ordered' },
    { value: 'prosecution_evidence',   label: 'Prosecution Evidence' },
    { value: 'defence_evidence',       label: 'Defence Evidence' },
    { value: 'final_arguments',        label: 'Final Arguments' },
    { value: 'judgment',               label: 'Judgment' },
  ],

  // ─── family_stages ── used by: family ───────────────────────────────────
  family_stages: [
    { value: 'petition_filed',         label: 'Petition Filed' },
    { value: 'notice_served',          label: 'Notice Served' },
    { value: 'reply_filed',            label: 'Reply Filed' },
    { value: 'mediation',              label: 'Mediation' },
    { value: 'evidence',               label: 'Evidence' },
    { value: 'arguments',              label: 'Final Arguments' },
    { value: 'decree',                 label: 'Decree' },
  ],

  // ─── maintenance_stages ── used by: maintenance ──────────────────────────
  // interim_order triggers "IA ACTIVE" indicator in UI (case within a case).
  maintenance_stages: [
    { value: 'petition_filed',         label: 'Petition Filed' },
    { value: 'notice_served',          label: 'Notice Served' },
    { value: 'interim_order',          label: 'Interim Maintenance Order' }, // ← IA ACTIVE flag
    { value: 'evidence',               label: 'Evidence' },
    { value: 'final_order',            label: 'Final Order' },
    { value: 'execution',              label: 'Execution' },
  ],

  // ─── guardianship_stages ── used by: guardianship ────────────────────────
  // welfare_officer_report is conditional (contested cases only).
  // periodic_review prompts Review Calendar Event creation.
  guardianship_stages: [
    { value: 'petition_filed',            label: 'Petition Filed' },
    { value: 'notice_served',             label: 'Notice Served' },
    { value: 'welfare_officer_report',    label: 'Welfare Officer Report', isConditional: true },
    { value: 'reply_filed',               label: 'Reply Filed' },
    { value: 'evidence',                  label: 'Evidence' },
    { value: 'interim_custody_order',     label: 'Interim Custody Order' },
    { value: 'final_order',               label: 'Final Order' },
    { value: 'periodic_review',           label: 'Periodic Review' },
  ],

  // ─── dv_stages ── used by: domestic_violence ─────────────────────────────
  dv_stages: [
    { value: 'application_filed',         label: 'Application Filed' },
    { value: 'protection_officer_report', label: 'Protection Officer Report' },
    { value: 'notice_to_respondent',      label: 'Notice to Respondent' },
    { value: 'interim_protection_order',  label: 'Interim Protection Order' },
    { value: 'hearing',                   label: 'Hearing' },
    { value: 'final_order',               label: 'Final Order' },
  ],

  // ─── muslim_stages ── used by: muslim_personal_law ───────────────────────
  muslim_stages: [
    { value: 'petition_filed',            label: 'Petition Filed' },
    { value: 'notice_served',             label: 'Notice Served' },
    { value: 'reply_filed',               label: 'Reply Filed' },
    { value: 'evidence',                  label: 'Evidence' },
    { value: 'arguments',                 label: 'Final Arguments' },
    { value: 'decree',                    label: 'Decree' },
  ],

  // ─── succession_stages ── used by: succession ────────────────────────────
  succession_stages: [
    { value: 'petition_filed',            label: 'Petition Filed' },
    { value: 'citation_issued',           label: 'Citation Issued' },
    { value: 'objections',               label: 'Objections' },
    { value: 'evidence',                  label: 'Evidence' },
    { value: 'arguments',                 label: 'Final Arguments' },
    { value: 'order',                     label: 'Order' },
    { value: 'letters_of_administration', label: 'Letters of Administration' },
  ],

  // ─── arbitration_stages ── used by: arbitration ──────────────────────────
  arbitration_stages: [
    { value: 'notice_of_arbitration',     label: 'Notice of Arbitration' },
    { value: 'statement_of_claim',        label: 'Statement of Claim' },
    { value: 'counter_claim',             label: 'Counter Claim' },
    { value: 'preliminary_hearing',       label: 'Preliminary Hearing' },
    { value: 'evidence',                  label: 'Evidence' },
    { value: 'final_hearing',             label: 'Final Hearing' },
    { value: 'award',                     label: 'Award' },
  ],

  // ─── company_stages ── used by: company ──────────────────────────────────
  company_stages: [
    { value: 'petition_filed',            label: 'Petition Filed' },
    { value: 'admission',                 label: 'Admission' },
    { value: 'notice_to_respondent',      label: 'Notice to Respondent' },
    { value: 'reply_filed',               label: 'Reply Filed' },
    { value: 'hearing',                   label: 'Hearing' },
    { value: 'order',                     label: 'Order' },
  ],

  // ─── insolvency_stages ── used by: insolvency ────────────────────────────
  insolvency_stages: [
    { value: 'application_filed',         label: 'Application Filed' },
    { value: 'admission',                 label: 'Admission' },
    { value: 'irp_appointed',             label: 'IRP Appointed' },
    { value: 'public_announcement',       label: 'Public Announcement' },
    { value: 'coc_constituted',           label: 'CoC Constituted' },
    { value: 'resolution_plan',           label: 'Resolution Plan Filed' },
    { value: 'coc_approval',              label: 'CoC Approval' },
    { value: 'nclt_order',               label: 'NCLT Order' },
  ],

  // ─── sarfaesi_stages ── used by: sarfaesi ────────────────────────────────
  sarfaesi_stages: [
    { value: 'demand_notice',             label: 'Demand Notice' },
    { value: 'possession_notice',         label: 'Possession Notice' },
    { value: 'sa_filed_at_drt',          label: 'SA Filed at DRT' },
    { value: 'reply_by_bank',             label: 'Reply by Bank' },
    { value: 'drt_hearing',              label: 'DRT Hearing' },
    { value: 'drt_order',               label: 'DRT Order' },
    { value: 'drat_appeal',             label: 'DRAT Appeal' },
  ],

  // ─── ip_stages ── used by: ip ────────────────────────────────────────────
  ip_stages: [
    { value: 'application_filed',         label: 'Application Filed' },
    { value: 'examination_report',        label: 'Examination Report' },
    { value: 'reply_to_report',           label: 'Reply to Report' },
    { value: 'opposition_filed',          label: 'Opposition Filed' },
    { value: 'hearing',                   label: 'Hearing' },
    { value: 'order',                     label: 'Order' },
  ],

  // ─── service_stages ── used by: service_cat, service_state_tribunal ──────
  // representation_filed is skippable with reason. interim_stay is heavyweight.
  service_stages: [
    { value: 'representation_filed',      label: 'Departmental Representation', isSkippable: true },
    { value: 'oa_filed',                  label: 'OA / MA Filed' },
    { value: 'interim_stay',              label: 'Interim Stay / Status Quo' }, // ← heavyweight milestone
    { value: 'counter_affidavit',         label: 'Counter Affidavit' },
    { value: 'rejoinder',                 label: 'Rejoinder' },
    { value: 'hearing',                   label: 'Final Hearing' },
    { value: 'order',                     label: 'Order' },
    { value: 'execution',                 label: 'Execution / Contempt' },
  ],

  // ─── armed_forces_stages ── used by: armed_forces ────────────────────────
  armed_forces_stages: [
    { value: 'petition_filed',            label: 'Petition Filed' },
    { value: 'notice_served',             label: 'Notice Served' },
    { value: 'reply_affidavit',           label: 'Reply Affidavit' },
    { value: 'hearing',                   label: 'Hearing' },
    { value: 'order',                     label: 'Order' },
  ],

  // ─── writ_stages ── shared by: writ, service_hc, service_sc, psc_recruitment
  writ_stages: [
    { value: 'petition_filed',            label: 'Petition Filed' },
    { value: 'admission_hearing',         label: 'Admission Hearing' },
    { value: 'counter_affidavit',         label: 'Counter Affidavit' },
    { value: 'rejoinder',                 label: 'Rejoinder / Sur-Rejoinder' },
    { value: 'main_hearing',              label: 'Main Hearing' },
    { value: 'judgment_reserved',         label: 'Judgment Reserved' },
    { value: 'judgment',                  label: 'Judgment Pronounced' },
    { value: 'slp',                       label: 'SLP Filed' },
    { value: 'sc_order',                  label: 'SC Order' },
  ],

  // ─── consumer_stages ── used by: consumer ────────────────────────────────
  consumer_stages: [
    { value: 'complaint_filed',           label: 'Complaint Filed' },
    { value: 'notice_to_opposite_party',  label: 'Notice to Opposite Party' },
    { value: 'reply_filed',               label: 'Reply Filed' },
    { value: 'evidence',                  label: 'Evidence / Affidavit' },
    { value: 'arguments',                 label: 'Final Arguments' },
    { value: 'order',                     label: 'Order' },
  ],

  // ─── labour_stages ── used by: labour ────────────────────────────────────
  labour_stages: [
    { value: 'conciliation',              label: 'Conciliation' },
    { value: 'reference_to_tribunal',     label: 'Reference to Tribunal / Court' },
    { value: 'statement_of_claim',        label: 'Statement of Claim' },
    { value: 'reply',                     label: 'Reply' },
    { value: 'evidence',                  label: 'Evidence' },
    { value: 'award',                     label: 'Award / Order' },
  ],

  // ─── tax_stages ── used by: tax ──────────────────────────────────────────
  tax_stages: [
    { value: 'assessment_order',          label: 'Assessment Order' },
    { value: 'objection_filed',           label: 'Objection / Rectification Filed' },
    { value: 'appeal_cit',               label: 'Appeal to CIT(A) / GSTAT' },
    { value: 'cit_order',               label: 'CIT(A) / GSTAT Order' },
    { value: 'tribunal_appeal',           label: 'Tribunal Appeal (ITAT/CESTAT)' },
    { value: 'tribunal_order',            label: 'Tribunal Order' },
    { value: 'hc_appeal',               label: 'High Court Appeal' },
    { value: 'hc_order',                 label: 'HC Order' },
  ],

  // ─── rera_stages ── used by: rera ────────────────────────────────────────
  rera_stages: [
    { value: 'complaint_filed',           label: 'Complaint Filed' },
    { value: 'notice_to_promoter',        label: 'Notice to Promoter' },
    { value: 'reply_filed',               label: 'Reply Filed' },
    { value: 'hearing',                   label: 'Hearing' },
    { value: 'order',                     label: 'Order' },
    { value: 'execution',                 label: 'Execution / Recovery' },
  ],

  // ─── ngt_stages ── used by: ngt ──────────────────────────────────────────
  ngt_stages: [
    { value: 'petition_filed',            label: 'Petition Filed' },
    { value: 'notice',                    label: 'Notice Issued' },
    { value: 'reply',                     label: 'Reply Filed' },
    { value: 'hearing',                   label: 'Hearing' },
    { value: 'order',                     label: 'Order' },
    { value: 'compliance',                label: 'Compliance' },
  ],

  // ─── mact_stages ── used by: motor_accident ──────────────────────────────
  mact_stages: [
    { value: 'claim_filed',               label: 'Claim Filed' },
    { value: 'notice_to_insurer',         label: 'Notice to Insurer / Respondent' },
    { value: 'reply_filed',               label: 'Reply Filed' },
    { value: 'evidence',                  label: 'Evidence / Medical Reports' },
    { value: 'arguments',                 label: 'Final Arguments' },
    { value: 'award',                     label: 'Award' },
    { value: 'execution',                 label: 'Execution / Recovery' },
    { value: 'paid_satisfied',            label: 'Award Paid / Satisfied' },
  ],

  // ─── quasi_judicial_stages ── used by: quasi_judicial ────────────────────
  // quasiJudicialForum sub-field records the specific forum from national datalist.
  quasi_judicial_stages: [
    { value: 'application_filed',         label: 'Application / Show Cause Filed' },
    { value: 'reply_filed',               label: 'Reply Filed' },
    { value: 'personal_hearing',          label: 'Personal Hearing' },
    { value: 'order',                     label: 'Order / Award' },
    { value: 'appeal',                    label: 'Appeal' },
  ],

  // ─── lok_adalat_stages ── used by: lok_adalat ────────────────────────────
  lok_adalat_stages: [
    { value: 'reference',                 label: 'Reference to Lok Adalat' },
    { value: 'mediation_session',         label: 'Mediation Session' },
    { value: 'award',                     label: 'Award / Settlement' },
  ],

  // ─── notarial_stages ── used by: notarial ────────────────────────────────
  notarial_stages: [
    { value: 'application',               label: 'Application Filed at SRO' },
    { value: 'scrutiny',                  label: 'Scrutiny / Objections' },
    { value: 'registration',              label: 'Registration / Execution' },
    { value: 'delivery',                  label: 'Document Delivered' },
  ],
};

// ── Helper: get stage list for a category slug ───────────────────────────────
export function getStagesForCategory(categorySlug) {
  const cat = CASE_CATEGORIES.find(c => c.slug === categorySlug);
  if (!cat || !cat.stageMap) return [];
  return CASE_STAGES[cat.stageMap] || [];
}

// ── Helper: get stage label for display ─────────────────────────────────────
export function getStageLabelForCategory(categorySlug, stageValue) {
  const stages = getStagesForCategory(categorySlug);
  const found = stages.find(s => s.value === stageValue);
  return found ? found.label : stageValue;
}

// ── Helper: get category object by slug ─────────────────────────────────────
export function getCategoryBySlug(slug) {
  return CASE_CATEGORIES.find(c => c.slug === slug) || null;
}

// ── Helper: get category label by slug ──────────────────────────────────────
export function getCategoryLabel(slug) {
  const cat = getCategoryBySlug(slug);
  return cat ? cat.label : slug;
}

// ── Helper: get cluster label by slug ───────────────────────────────────────
export function getClusterLabel(clusterSlug) {
  const cluster = CASE_CLUSTERS.find(c => c.slug === clusterSlug);
  return cluster ? cluster.label : clusterSlug;
}

// ── Helper: filter categories by cluster slug ────────────────────────────────
export function getCategoriesByCluster(clusterSlug) {
  return CASE_CATEGORIES.filter(c => c.cluster === clusterSlug);
}

// ── Helper: validate a category slug against the master list ─────────────────
export function isValidCategorySlug(slug) {
  return CASE_CATEGORIES.some(c => c.slug === slug);
}

// ── Helper: derive cluster from category slug (never user-entered) ───────────
export function deriveCluster(categorySlug) {
  return CATEGORY_TO_CLUSTER[categorySlug] || null;
}

// ────────────────────────────────────────────────────────────────────────────
// QUASI-JUDICIAL FORUM MASTER — All-India list (Chairman-validated, June 2026)
// ────────────────────────────────────────────────────────────────────────────
export const QUASI_JUDICIAL_FORUMS = [
  // Central Regulatory Bodies
  'SEBI — Securities and Exchange Board of India',
  'RBI — Reserve Bank of India',
  'IRDAI — Insurance Regulatory and Development Authority',
  'TRAI — Telecom Regulatory Authority of India',
  'CCI — Competition Commission of India',
  'CERC — Central Electricity Regulatory Commission',
  'PFRDA — Pension Fund Regulatory and Development Authority',
  'MCI / NMC — Medical Council of India / NMC',
  'BCI — Bar Council of India',
  'ICAI — Disciplinary Committee',
  // Tax & Customs Authorities
  'Income Tax Appellate Tribunal (ITAT)',
  'GST Appellate Authority',
  'Customs, Excise and Service Tax Appellate Tribunal (CESTAT)',
  'Authority for Advance Rulings (AAR)',
  // Labour & Employment
  'Central Government Industrial Tribunal (CGIT)',
  'Employees Provident Fund Appellate Tribunal',
  'Employees State Insurance Court',
  // Land & Urban
  'RERA — Real Estate Regulatory Authority (State)',
  'National Green Tribunal (NGT)',
  'Land Acquisition Collector',
  'District Forum (Revenue)',
  // Banking & Finance
  'Debt Recovery Tribunal (DRT)',
  'Debt Recovery Appellate Tribunal (DRAT)',
  'NCLT — National Company Law Tribunal',
  // State Regulatory Bodies
  'State Electricity Regulatory Commission (SERC)',
  'State Bar Council — Disciplinary Committee',
  'State Pollution Control Board',
  'MSME Facilitation Council',
  // Consumer Redressal
  'District Consumer Disputes Redressal Commission (DCDRC)',
  'State Consumer Disputes Redressal Commission (SCDRC)',
  // Aviation, Shipping, Petroleum
  'Directorate General of Civil Aviation (DGCA)',
  'Petroleum and Natural Gas Regulatory Board (PNGRB)',
  // Education
  'University Tribunal',
  'Medical Admission Regulatory Authority',
  // Other
  'Department of Telecommunications (DoT)',
  'Information Commissioner (RTI Appeals)',
  'Lokayukta / ACB Inquiry',
  'District Grievance Redressal Committee',
  'Other — specify in notes',
];

// ============================================
// CASE STATUS LIFECYCLE — 11 Values
// ============================================
// ADR-CASE-STAGE-001: Status = lifecycle health ONLY.
// Retired: Admitted, In Progress, Hearing, Adjourned, Soft Close Pending
// (those were procedural — they belong in Stage, not Status).
// ============================================
export const CASE_STATUS_LIST = [
  'Draft',
  'Pending',
  'Active',
  'On Hold',
  'Order Reserved',
  'Decided',
  'Disposed',
  'Withdrawn',
  'Dismissed',
  'Settled',
  'Closed',
];

export const CASE_STATUSES = {
  DRAFT:           'Draft',
  PENDING:         'Pending',
  ACTIVE:          'Active',
  ON_HOLD:         'On Hold',
  ORDER_RESERVED:  'Order Reserved',
  DECIDED:         'Decided',
  DISPOSED:        'Disposed',
  WITHDRAWN:       'Withdrawn',
  DISMISSED:       'Dismissed',
  SETTLED:         'Settled',
  CLOSED:          'Closed',
};

// ✅ ADR-CASE-STAGE-001: Full status options for dropdowns — 11 statuses
export const CASE_STATUS_OPTIONS = [
  { value: 'Draft',          label: 'Draft',          color: 'bg-slate-100 text-slate-700' },
  { value: 'Pending',        label: 'Pending',        color: 'bg-yellow-100 text-yellow-800' },
  { value: 'Active',         label: 'Active',         color: 'bg-blue-100 text-blue-800' },
  { value: 'On Hold',        label: 'On Hold',        color: 'bg-gray-100 text-gray-800' },
  { value: 'Order Reserved', label: 'Order Reserved', color: 'bg-purple-100 text-purple-800' },
  { value: 'Decided',        label: 'Decided',        color: 'bg-emerald-100 text-emerald-800' },
  { value: 'Disposed',       label: 'Disposed',       color: 'bg-slate-200 text-slate-700' },
  { value: 'Withdrawn',      label: 'Withdrawn',      color: 'bg-red-100 text-red-800' },
  { value: 'Dismissed',      label: 'Dismissed',      color: 'bg-orange-100 text-orange-800' },
  { value: 'Settled',        label: 'Settled',        color: 'bg-green-100 text-green-800' },
  { value: 'Closed',         label: 'Closed',         color: 'bg-teal-100 text-teal-800' },
];

// STATUS_PERMISSIONS — who can set which status
// Terminal statuses require Partner+ (irreversible actions)
export const STATUS_PERMISSIONS = {
  'Draft':          ['Admin', 'ManagingPartner', 'Partner', 'SrAssociate'],
  'Pending':        ['Admin', 'ManagingPartner', 'Partner', 'SrAssociate'],
  'Active':         ['Admin', 'ManagingPartner', 'Partner', 'SrAssociate'],
  'On Hold':        ['Admin', 'ManagingPartner', 'Partner', 'SrAssociate'],
  'Order Reserved': ['Admin', 'ManagingPartner', 'Partner', 'SrAssociate'],
  'Decided':        ['Admin', 'ManagingPartner', 'Partner'],
  'Disposed':       ['Admin', 'ManagingPartner', 'Partner'],
  'Withdrawn':      ['Admin', 'ManagingPartner', 'Partner'],
  'Dismissed':      ['Admin', 'ManagingPartner', 'Partner'],
  'Settled':        ['Admin', 'ManagingPartner', 'Partner'],
  'Closed':         ['Admin', 'ManagingPartner', 'Partner'],
};

// VALID_STATUS_TRANSITIONS — rebuilt for 11 statuses
// ADR-CASE-STAGE-001: removed all 5 retired statuses.
export const VALID_STATUS_TRANSITIONS = {
  'Draft':          ['Pending', 'Active', 'Withdrawn'],
  'Pending':        ['Active', 'On Hold', 'Withdrawn', 'Dismissed'],
  'Active':         ['On Hold', 'Order Reserved', 'Decided', 'Disposed', 'Withdrawn', 'Dismissed', 'Settled', 'Closed'],
  'On Hold':        ['Active', 'Withdrawn', 'Dismissed', 'Closed'],
  'Order Reserved': ['Decided', 'Disposed', 'Active'],
  // Terminal statuses — no transitions out
  'Decided':        [],
  'Disposed':       [],
  'Withdrawn':      [],
  'Dismissed':      [],
  'Settled':        [],
  'Closed':         [],
};

// TERMINAL_STATUSES — 6 terminal statuses
export const TERMINAL_STATUSES = ['Decided', 'Disposed', 'Withdrawn', 'Dismissed', 'Settled', 'Closed'];

// ARCHIVABLE_STATUSES — cases can be archived only when terminal
export const ARCHIVABLE_STATUSES = ['Decided', 'Disposed', 'Withdrawn', 'Dismissed', 'Settled', 'Closed'];

// Status helpers
export function canSetStatus(userRole, targetStatus) {
  const allowedRoles = STATUS_PERMISSIONS[targetStatus];
  return allowedRoles ? allowedRoles.includes(userRole) : false;
}

export function isValidTransition(currentStatus, targetStatus) {
  const validNext = VALID_STATUS_TRANSITIONS[currentStatus];
  return validNext ? validNext.includes(targetStatus) : false;
}

export function getAvailableStatuses(userRole, currentStatus) {
  const validTransitions = VALID_STATUS_TRANSITIONS[currentStatus] || [];
  return validTransitions.filter(status => canSetStatus(userRole, status));
}

export function getCaseStatusColor(status) {
  const statusObj = CASE_STATUS_OPTIONS.find(s => s.value === status);
  return statusObj ? statusObj.color : 'bg-gray-100 text-gray-800';
}

export function isCaseTerminal(status) {
  return TERMINAL_STATUSES.includes(status);
}

export function isCaseArchivable(status) {
  return ARCHIVABLE_STATUSES.includes(status);
}

// ── DRAFT_CATEGORIES: aliased to CASE_CATEGORIES for B3 migration ─────────────
// Phase B3 will replace all DRAFT_CATEGORIES references with CASE_CATEGORIES.
// This alias prevents runtime errors during Phase A and B until B3 is deployed.
export const DRAFT_CATEGORIES = CASE_CATEGORIES.map(cat => cat.label);

// ============================================
// TEMPLATE TYPES (AI-Ready)
// ============================================
export const TEMPLATE_TYPES = [
  { value: 'petition',         label: 'Petition' },
  { value: 'application',      label: 'Application' },
  { value: 'affidavit',        label: 'Affidavit' },
  { value: 'notice',           label: 'Notice' },
  { value: 'agreement',        label: 'Agreement' },
  { value: 'order',            label: 'Order' },
  { value: 'memo',             label: 'Memorandum' },
  { value: 'submission',       label: 'Submission' },
  { value: 'reply',            label: 'Reply' },
  { value: 'rejoinder',        label: 'Rejoinder' },
  { value: 'vakalatnama',      label: 'Vakalatnama' },
  { value: 'power_of_attorney',label: 'Power of Attorney' },
  { value: 'appeal',           label: 'Appeal' },
  { value: 'revision',         label: 'Revision' },
  { value: 'suit',             label: 'Suit' },
];

export function getTemplateTypeLabel(value) {
  const found = TEMPLATE_TYPES.find(t => t.value === value);
  return found ? found.label : value;
}

// ============================================
// CASE CONTEXT FIELDS — SPRINT-DRAFT-EDITOR-001 (Jul 2026)
// Single source of truth for template tagging (TemplateTaggingPage.jsx)
// and for what functions/drafts/getCaseContext.js actually returns.
// If you add a field to getCaseContext.js, add it here too, or it won't
// show up as an option in the tagging tool.
// ============================================
export const CASE_CONTEXT_FIELDS = [
  { sourceField: 'case.title',                label: 'Case Title (e.g. "X vs Y")' },
  { sourceField: 'case.caseNumber',            label: 'Case Number' },
  { sourceField: 'case.caseYear',              label: 'Case Year' },
  { sourceField: 'case.court',                 label: 'Court' },
  { sourceField: 'case.caseType',              label: 'Case Type' },
  { sourceField: 'case.category',              label: 'Case Category' },
  { sourceField: 'case.stage',                 label: 'Case Stage' },
  { sourceField: 'case.filingDate',            label: 'Filing Date' },
  { sourceField: 'case.description',           label: 'Case Description' },
  { sourceField: 'case.petitionerName',        label: 'Petitioner Name' },
  { sourceField: 'case.respondentName',        label: 'Respondent Name' },
  { sourceField: 'case.appellantName',         label: 'Appellant Name' },
  { sourceField: 'case.defendantName',         label: 'Defendant Name' },
  { sourceField: 'case.oppositePartyName',     label: 'Opposite Party Name' },
  { sourceField: 'case.oppositeAdvocate',      label: 'Opposite Advocate' },
  { sourceField: 'case.advocateOnRecordName',  label: 'Advocate on Record' },

  { sourceField: 'client.name',                label: 'Client Name' },
  { sourceField: 'client.companyName',         label: 'Client Company Name' },
  { sourceField: 'client.contactPersonName',   label: 'Client Contact Person' },
  { sourceField: 'client.email',               label: 'Client Email' },
  { sourceField: 'client.phone',               label: 'Client Phone' },
  { sourceField: 'client.altPhone',             label: 'Client Alternate Phone' },
  { sourceField: 'client.address',             label: 'Client Address' },
  { sourceField: 'client.type',                label: 'Client Type' },

  { sourceField: 'firm.name',                  label: 'Firm Name' },
  { sourceField: 'firm.address',                label: 'Firm Address' },
  { sourceField: 'firm.gstNumber',              label: 'Firm GST Number' },
  { sourceField: 'firm.panNumber',              label: 'Firm PAN Number' },
  { sourceField: 'firm.barRegistrationNumber',  label: 'Firm Bar Registration Number' },
  { sourceField: 'firm.authorizedSignatory',    label: 'Firm Authorized Signatory' },
  { sourceField: 'firm.phone',                  label: 'Firm Phone' },
  { sourceField: 'firm.contactEmail',            label: 'Firm Contact Email' },

  { sourceField: 'today.date',                  label: "Today's Date (long form)" },
  { sourceField: 'today.isoDate',                label: "Today's Date (YYYY-MM-DD)" },
  { sourceField: 'today.year',                    label: 'Current Year' },
];

// ============================================
// DRAFT GENERATION BADGES — SPRINT-DRAFT-EDITOR-001 (Jul 2026)
// Labels for the assignedDrafts.generationMethod / aiEnhanced / citationsFailed
// fields, used by CaseDrafts.jsx to badge generated vs manually-assigned drafts.
// ============================================
export const DRAFT_GENERATION_LABELS = {
  auto_fill:   'Auto-fill only',
  ai_enhanced: 'AI-enhanced',
};

// ============================================
// COURT TYPES
// ============================================
export const COURT_TYPES = [
  { value: 'DistrictCourt', label: 'District Court' },
  { value: 'HighCourt',     label: 'High Court' },
  { value: 'SupremeCourt',  label: 'Supreme Court' },
  { value: 'Tribunal',      label: 'Tribunal' }
];

export function getCourtTypeLabel(value) {
  const found = COURT_TYPES.find(c => c.value === value);
  return found ? found.label : value;
}

// ============================================
// USER ROLES
// ============================================
export const USER_ROLES = [
  { value: 'Admin',          label: 'Admin' },
  { value: 'ManagingPartner',label: 'Managing Partner' },
  { value: 'Partner',        label: 'Partner' },
  { value: 'SrAssociate',    label: 'Senior Associate' },
  { value: 'Associate',      label: 'Associate' },
  { value: 'Intern',         label: 'Intern' },
  { value: 'Receptionist',   label: 'Receptionist' },
  { value: 'Accountant',     label: 'Accountant' }
];

export const LEGAL_ROLES = [
  'Admin', 'ManagingPartner', 'Partner', 'SrAssociate', 'Associate', 'Intern'
];

export const NON_LEGAL_ROLES = ['Accountant', 'Receptionist'];

// ============================================
// GENDER OPTIONS
// ============================================
export const GENDER_OPTIONS = [
  { value: 'male',              label: 'Male' },
  { value: 'female',            label: 'Female' },
  { value: 'non_binary',        label: 'Non-Binary' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

// ============================================
// FILE UPLOAD CONSTANTS
// ============================================
export const ALLOWED_DRAFT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'image/jpeg',
  'image/png',
];

export const FILE_LIMITS = {
  MAX_SIZE_BYTES: 20 * 1024 * 1024,
  MAX_SIZE_LABEL: '20 MB',
  MAX_FILES:      10,
};

export function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ============================================
// ROLE HIERARCHY & PERMISSIONS
// ============================================
export const ROLE_HIERARCHY = {
  'Admin':           100,
  'ManagingPartner': 100,
  'Partner':          80,
  'SrAssociate':      60,
  'Associate':        40,
  'Intern':           20,
  'Receptionist':     30,
  'Accountant':       50
};

// ============================================
// FEATURE FLAGS & PLAN DEFAULTS
// ============================================
export const FEATURE_FLAGS = {
  CLIENT_PORTAL:   'portal',
  DRAFTS:          'drafts',
  BULK_IMPORT:     'bulk_import',
  AI:              'ai',
  KANOON_INT:      'kanoon_int',
  ACCOUNTING_INT:  'accounting_int',
};

export const PLAN_DEFAULTS = {
  starter:      [],
  professional: ['portal', 'drafts', 'bulk_import'],
  enterprise:   ['portal', 'drafts', 'bulk_import', 'ai', 'kanoon_int', 'accounting_int'],
  custom:       [],
};

export const PLAN_LABELS = {
  starter:      'Starter',
  professional: 'Professional',
  enterprise:   'Enterprise',
  custom:       'Custom',
};

export const FEATURE_META = {
  portal: {
    label:       'Client Portal',
    description: 'Magic-link client access to view cases, invoices, and documents',
    plans:       ['professional', 'enterprise'],
  },
  drafts: {
    label:       'Draft Library',
    description: 'Upload, assign, and manage legal drafts linked to cases',
    plans:       ['professional', 'enterprise'],
  },
  bulk_import: {
    label:       'Bulk Import & eCourts',
    description: 'Import cases from CSV/Excel and sync with eCourts live data',
    plans:       ['professional', 'enterprise'],
  },
  ai: {
    label:       'AI Case Support',
    description: 'Gemini-powered case research and draft assistance',
    plans:       ['enterprise'],
  },
  kanoon_int: {
    label:       'IndiaKanoon Integration',
    description: 'Search and link judgments directly from IndiaKanoon',
    plans:       ['enterprise'],
  },
  accounting_int: {
    label:       'Accounting Integrations',
    description: 'Sync invoices with Zoho Books, Tally, or QuickBooks',
    plans:       ['enterprise'],
  },
};

export const CASE_ASSIGNMENT_ROLES = [
  'Admin', 'ManagingPartner', 'Partner', 'SrAssociate'
];

export const FINANCIAL_ACCESS_ROLES = [
  'Admin', 'ManagingPartner', 'Accountant'
];

export const USER_MANAGEMENT_ROLES = [
  'Admin', 'ManagingPartner'
];

// ============================================
// PAYMENT METHODS
// ============================================
export const PAYMENT_METHODS = [
  { value: 'Cash',         label: 'Cash' },
  { value: 'Cheque',       label: 'Cheque' },
  { value: 'UPI',          label: 'UPI' },
  { value: 'BankTransfer', label: 'Bank Transfer' },
  { value: 'NEFT',         label: 'NEFT' },
  { value: 'RTGS',         label: 'RTGS' },
  { value: 'IMPS',         label: 'IMPS' },
];

// ============================================
// GST / TAX CONSTANTS
// ============================================
export const GST_RATE  = 18;
export const CGST_RATE =  9;
export const SGST_RATE =  9;

export const SAC_CODES = [
  { value: '998211', label: '998211 — Legal advisory and representation services' },
  { value: '998212', label: '998212 — Legal documentation and certification' },
  { value: '998213', label: '998213 — Arbitration and conciliation services' },
  { value: '998214', label: '998214 — Other legal services' },
  { value: 'NONE',   label: 'NONE — No GST (exempt / non-taxable)' },
];

// ============================================
// SCHEMA VERSIONING
// ============================================
export const SCHEMA_VERSIONS = {
  case:    2,
  client:  1,
  invoice: 1,
  payment: 1,
  lead:    1,
  user:    1,
};

// ============================================
// ERROR CODES
// ============================================
// ============================================
// ORIGINALS — ORIGINAL DOCUMENT CUSTODY TRACKING
// SPRINT-ORIGINALS-001 (Jul 2026) · ADR-ORIGINALS-001 v1.0 — FROZEN
// Category list is a starting proposal per ADR §3.2 — extensible without
// a schema change; still open for the Chairman's line-by-line validation
// against actual Kalluri Associates practice (ADR §6, not sprint-blocking).
// ============================================
export const ORIGINAL_SOURCE_TYPES = [
  { value: 'court_filed',     label: 'Court-Filed Original' },
  { value: 'client_original', label: 'Client-Held Original' },
];

export const ORIGINAL_DOCUMENT_CATEGORIES = [
  { value: 'vakalatnama',        label: 'Vakalatnama',         typicalSource: 'court_filed' },
  { value: 'affidavit',          label: 'Affidavit',           typicalSource: 'court_filed' },
  { value: 'sale_deed',          label: 'Sale Deed',           typicalSource: 'client_original' },
  { value: 'will',                label: 'Will / Testament',    typicalSource: 'client_original' },
  { value: 'agreement',          label: 'Agreement / Contract', typicalSource: 'client_original' },
  { value: 'promissory_note',    label: 'Promissory Note',      typicalSource: 'client_original' },
  { value: 'share_certificate',  label: 'Share Certificate',    typicalSource: 'client_original' },
  { value: 'power_of_attorney',  label: 'Power of Attorney',    typicalSource: 'either' },
  { value: 'other',              label: 'Other',                typicalSource: 'either' },
];

export function getOriginalCategoryLabel(value) {
  const found = ORIGINAL_DOCUMENT_CATEGORIES.find(c => c.value === value);
  return found ? found.label : value;
}

// custodyStatus enum — deliberately includes 'released' now (unwired this
// sprint) so ADR-ORIGINALS-002's release workflow is additive, not a
// breaking migration. Do not build any UI action for 'released' yet.
export const CUSTODY_STATUS = {
  IN_STORAGE:         'in_storage',
  CHECKED_OUT:        'checked_out',
  WITH_COURT:         'with_court',
  WITH_EXTERNAL_PARTY:'with_external_party',
  RELEASED:           'released',  // reserved — not wired to any UI action this sprint
};

export const CUSTODY_STATUS_LABELS = {
  [CUSTODY_STATUS.IN_STORAGE]:          'In Storage',
  [CUSTODY_STATUS.CHECKED_OUT]:         'Checked Out',
  [CUSTODY_STATUS.WITH_COURT]:          'With Court',
  [CUSTODY_STATUS.WITH_EXTERNAL_PARTY]: 'With External Party',
  [CUSTODY_STATUS.RELEASED]:            'Released',
};

export function getCustodyStatusLabel(value) {
  return CUSTODY_STATUS_LABELS[value] || value;
}

// custodyLog action types — append-only event log (BS-005 doctrine)
export const CUSTODY_LOG_ACTIONS = {
  INTAKE:            'intake',
  CHECKOUT:          'checkout',
  CHECKIN:           'checkin',
  LOCATION_CHANGE:   'location_change',
  RETURNED_BY_COURT: 'returned_by_court',
  VERIFIED:          'verified',
};

// Roles permitted to perform custody actions (checkout/checkin/verify/court-return).
// Receptionist is deliberately excluded here — create-only per ADR §5.
export const CUSTODY_ACTION_ROLES = [
  'Admin', 'ManagingPartner', 'Partner', 'SrAssociate', 'Associate'
];

// Roles permitted to create an originals record (intake) — broadest group.
export const ORIGINAL_INTAKE_ROLES = [
  'Admin', 'ManagingPartner', 'Partner', 'SrAssociate', 'Associate', 'Receptionist'
];

// Roles permitted to edit storage location / category after intake — narrower,
// matches firestore.rules isOriginalStorageOrCategoryOnly() arm.
export const ORIGINAL_EDIT_ROLES = [
  'Admin', 'ManagingPartner', 'Partner', 'SrAssociate'
];


// ============================================
// SPRINT-DPDP-DATA-RIGHTS-001 (Jul 22, 2026)
// ADR-DPDP-DATA-RIGHTS-001 (FROZEN, Jul 22, 2026) — Sections 3, 4, 8, 8a
// ============================================

// Section 8 — the 8-value erasure request state machine.
export const ERASURE_REQUEST_STATES = {
  SUBMITTED:                    'Submitted',
  IDENTITY_VERIFICATION_PENDING:'Identity Verification Pending',
  PENDING_REVIEW:               'Pending Review',
  NEEDS_MORE_INFORMATION:       'Needs More Information',
  WITHDRAWN:                    'Withdrawn',
  APPROVED_EXECUTING:           'Approved — Executing',
  FULFILLED:                    'Fulfilled',
  REJECTED:                     'Rejected',
};

export const ERASURE_REQUEST_STATE_LIST = Object.values(ERASURE_REQUEST_STATES);

// States from which a requester may still withdraw (before execution starts).
export const ERASURE_WITHDRAWABLE_STATES = [
  ERASURE_REQUEST_STATES.SUBMITTED,
  ERASURE_REQUEST_STATES.IDENTITY_VERIFICATION_PENDING,
  ERASURE_REQUEST_STATES.PENDING_REVIEW,
  ERASURE_REQUEST_STATES.NEEDS_MORE_INFORMATION,
];

// Terminal states — no further transition permitted.
export const ERASURE_TERMINAL_STATES = [
  ERASURE_REQUEST_STATES.WITHDRAWN,
  ERASURE_REQUEST_STATES.FULFILLED,
  ERASURE_REQUEST_STATES.REJECTED,
];

// Section 4 — legalHoldSource enum. Legal Hold is a generic platform
// capability; "erasure_request" is only one of its consumers.
export const LEGAL_HOLD_SOURCES = {
  ERASURE_REQUEST:      'erasure_request',
  MANUAL_PARTNER_ACTION:'manual_partner_action',
  AUDIT:                'audit',
  REGULATORY:           'regulatory',
};

export const LEGAL_HOLD_SOURCE_LIST = Object.values(LEGAL_HOLD_SOURCES);

// Section 3 — statutory retention buckets. `years: null` means indefinite
// hold (requires manual Partner override to ever release — never auto-run).
export const RETENTION_BUCKETS = {
  CONTRACTS_RECOVERY:  { years: 3,    label: 'Contracts, Recovery & Invoices',        basis: 'Limitation Act Arts. 6–55' },
  TORT_NEGLIGENCE:      { years: 3,    label: 'Tort / Negligence Claims',              basis: 'Limitation Act Arts. 72–91' },
  IMMOVABLE_PROPERTY:   { years: 12,   label: 'Immovable Property & Mortgages',        basis: 'Limitation Act Arts. 61–67' },
  TAX_FINANCIAL:        { years: 8,    label: 'Tax & Financial Disputes',              basis: 'Income Tax Act Sec. 149' },
  MINOR_OFFENCES:       { years: 3,    label: 'Minor Offences (CrPC/BNSS)',            basis: 'Sec. 468 CrPC' },
  MAJOR_CRIMINAL_WRIT:  { years: null, label: 'Major Criminal Offence / Writ',         basis: 'No statute of limitations' },
};

// ✅ CONFIRMED — Chairman (Kalluri Associates) review complete, Jul 23, 2026.
// Approved from the Chairman's domain-review perspective per standing project
// governance (legal/procedural architectural decisions require sign-off
// before production use). Full text of the approval is recorded in
// DPDP-RETENTION-BUCKET-ChairmanReview.md alongside this sprint's docs.
//
// Adopted policy — deliberately conservative: only categories with a clear,
// unambiguous statutory retention period are mapped to a time-bound bucket
// below. Every category involving continuing rights, constitutional
// remedies, family matters, probate, writs, service matters, environmental
// proceedings, major criminal offences, or any other legally ambiguous
// proceeding is left unmapped and therefore falls through to the
// MAJOR_CRIMINAL_WRIT bucket's indefinite hold in getRetentionBucketForCategory()
// below — never auto-released, requires manual Partner override. Per the
// Chairman's stated rationale: retaining records slightly longer is always
// preferable to prematurely releasing data that may later matter to appeals,
// execution proceedings, restoration petitions, professional negligence
// claims, or regulatory inquiries. This mapping is final for Phase B —
// reopening it requires a new Chairman review, not just an engineering change.
export const CASE_CATEGORY_RETENTION_BUCKET = {
  // Contracts, Recovery & Invoices — 3 yrs
  civil:               'CONTRACTS_RECOVERY',
  money_recovery:      'CONTRACTS_RECOVERY',
  execution:           'CONTRACTS_RECOVERY',
  ni_act:              'CONTRACTS_RECOVERY',
  sarfaesi:            'CONTRACTS_RECOVERY', // secured-debt recovery — falls within "recovery"

  // Immovable Property & Mortgages — 12 yrs
  land_acquisition:    'IMMOVABLE_PROPERTY',
  rent_eviction:        'IMMOVABLE_PROPERTY',

  // Tax & Financial Disputes — 8 yrs
  tax:                 'TAX_FINANCIAL',

  // Minor Offences (CrPC/BNSS) — 3 yrs
  bail:                'MINOR_OFFENCES',

  // Everything else — indefinite / Manual Partner Review, by deliberate
  // Chairman-approved policy, not by omission. Includes (non-exhaustive):
  // injunction, revenue_agency, criminal, pocso, family, maintenance,
  // guardianship, domestic_violence, muslim_personal_law, succession,
  // arbitration, company, insolvency, ip, service_cat, service_state_tribunal,
  // service_hc, service_sc, armed_forces, psc_recruitment, writ, consumer,
  // labour, rera, ngt, motor_accident, quasi_judicial, lok_adalat, notarial,
  // miscellaneous.
};

export function getRetentionBucketForCategory(categorySlug) {
  const bucketKey = CASE_CATEGORY_RETENTION_BUCKET[categorySlug] || 'MAJOR_CRIMINAL_WRIT';
  return RETENTION_BUCKETS[bucketKey];
}

// CBDT Rule 6F + Sec. 149 reassessment window (ADR §3, invoices row).
export const INVOICE_RETENTION_YEARS = 8;

// Section 8a — RBAC permission nodes (not a forced DPO role). Default
// holders: Admin, ManagingPartner. Larger firms may later assign these same
// nodes to a custom DPO role via Team Management — no rules change required.
export const DPDP_PERMISSION_ROLES = {
  can_approve_data_erasure:  ['Admin', 'ManagingPartner'],
  can_handle_dpdp_grievances:['Admin', 'ManagingPartner'],
};

export const ERROR_CODES = {
  AUTH_NOT_AUTHENTICATED:  'AUTH_001',
  AUTH_INSUFFICIENT_ROLE:  'AUTH_002',
  AUTH_FIRM_MISMATCH:      'AUTH_003',
  AUTH_SESSION_EXPIRED:    'AUTH_004',
  AUTH_INVALID_TOKEN:      'AUTH_005',

  LICENSE_NOT_FOUND:       'LICENSE_001',
  LICENSE_EXPIRED:         'LICENSE_002',
  LICENSE_USER_LIMIT:      'LICENSE_003',
  LICENSE_FEATURE_BLOCKED: 'LICENSE_004',

  CASE_NOT_FOUND:          'CASE_001',
  CASE_INVALID_STATUS:     'CASE_002',
  CASE_ARCHIVE_BLOCKED:    'CASE_003',
  CASE_ALREADY_ARCHIVED:   'CASE_004',
  CASE_UNARCHIVE_BLOCKED:  'CASE_005',
  CASE_ASSIGN_FAILED:      'CASE_006',
  CASE_INVALID_STAGE:      'CASE_007',
  CASE_INVALID_CATEGORY:   'CASE_008',

  ORIGINAL_NOT_FOUND:            'ORIGINAL_001',
  ORIGINAL_INVALID_SOURCE_TYPE:  'ORIGINAL_002',
  ORIGINAL_INVALID_CATEGORY:     'ORIGINAL_003',
  ORIGINAL_ALREADY_CHECKED_OUT:  'ORIGINAL_004',
  ORIGINAL_NOT_CHECKED_OUT:      'ORIGINAL_005',
  ORIGINAL_STORAGE_LOCATION_REQUIRED: 'ORIGINAL_006',

  CLIENT_NOT_FOUND:         'CLIENT_001',
  CLIENT_DUPLICATE_EMAIL:   'CLIENT_002',
  CLIENT_INVALID_TYPE:      'CLIENT_003',
  CLIENT_PORTAL_FAILED:     'CLIENT_004',
  CLIENT_ALREADY_APPROVED:  'CLIENT_005',
  CLIENT_ALREADY_REJECTED:  'CLIENT_006',
  CLIENT_ALREADY_ARCHIVED:  'CLIENT_007',
  CLIENT_UNARCHIVE_BLOCKED: 'CLIENT_008',
  CLIENT_ARCHIVE_BLOCKED:   'CLIENT_009',

  LEAD_NOT_FOUND:           'LEAD_001',
  LEAD_INVALID_STATUS:      'LEAD_002',
  LEAD_CONVERT_FAILED:      'LEAD_003',

  INVOICE_NOT_FOUND:        'INVOICE_001',
  INVOICE_ALREADY_PAID:     'INVOICE_002',
  INVOICE_IMMUTABLE:        'INVOICE_003',
  INVOICE_INVALID_AMOUNT:   'INVOICE_004',
  INVOICE_ALREADY_VOIDED:   'INVOICE_005',
  INVOICE_OVERPAYMENT:      'INVOICE_006',

  PAYMENT_NOT_FOUND:        'PAYMENT_001',
  PAYMENT_IMMUTABLE:        'PAYMENT_002',
  PAYMENT_INVALID_AMOUNT:   'PAYMENT_003',
  PAYMENT_METHOD_INVALID:   'PAYMENT_004',
  PAYMENT_CHEQUE_CLEARED:   'PAYMENT_005',
  PAYMENT_CHEQUE_BOUNCED:   'PAYMENT_006',
  PAYMENT_NO_INVOICE_LINK:  'PAYMENT_007',

  USER_NOT_FOUND:           'USER_001',
  USER_DUPLICATE_EMAIL:     'USER_002',
  USER_INVALID_ROLE:        'USER_003',
  USER_DELETE_LAST_ADMIN:   'USER_004',
  USER_CLAIMS_SYNC_FAILED:  'USER_005',
  USER_DEACTIVATED:         'USER_006',

  CONFLICT_STALE_VERSION:    'CONFLICT_001',
  CONFLICT_IMMUTABLE_DOC:    'CONFLICT_002',
  CONFLICT_CONCURRENT_WRITE: 'CONFLICT_003',

  GEN_UNKNOWN:              'GEN_001',
  GEN_NETWORK:              'GEN_002',
  GEN_VALIDATION:           'GEN_003',
  GEN_NOT_IMPLEMENTED:      'GEN_004',
};

export const ERROR_MESSAGES = {
  [ERROR_CODES.AUTH_NOT_AUTHENTICATED]:  'Please log in to continue.',
  [ERROR_CODES.AUTH_INSUFFICIENT_ROLE]:  'You do not have permission to perform this action.',
  [ERROR_CODES.AUTH_FIRM_MISMATCH]:      "Access denied. You cannot access another firm's data.",
  [ERROR_CODES.AUTH_SESSION_EXPIRED]:    'Your session has expired. Please log in again.',
  [ERROR_CODES.AUTH_INVALID_TOKEN]:      'Authentication error. Please log out and log in again.',

  [ERROR_CODES.LICENSE_NOT_FOUND]:       'Firm license not found. Contact LexCore support.',
  [ERROR_CODES.LICENSE_EXPIRED]:         "Your firm's license has expired. Please renew to continue.",
  [ERROR_CODES.LICENSE_USER_LIMIT]:      'Your firm has reached its user limit. Upgrade your plan to add more users.',
  [ERROR_CODES.LICENSE_FEATURE_BLOCKED]: 'This feature is not available on your current plan. Please upgrade.',

  [ERROR_CODES.CASE_NOT_FOUND]:          'Case not found.',
  [ERROR_CODES.CASE_INVALID_STATUS]:     'This action is not allowed for the current case status.',
  [ERROR_CODES.CASE_ARCHIVE_BLOCKED]:    'Cannot archive this case — there are unpaid invoices.',
  [ERROR_CODES.CASE_ALREADY_ARCHIVED]:   'This case is already archived.',
  [ERROR_CODES.CASE_UNARCHIVE_BLOCKED]:  'Cannot restore this case. Please check required fields.',
  [ERROR_CODES.CASE_ASSIGN_FAILED]:      'Could not assign this case. Please check the selected user.',
  [ERROR_CODES.CASE_INVALID_STAGE]:      'Invalid stage for this case category.',
  [ERROR_CODES.CASE_INVALID_CATEGORY]:   'Invalid case category.',

  [ERROR_CODES.ORIGINAL_NOT_FOUND]:            'Original document record not found.',
  [ERROR_CODES.ORIGINAL_INVALID_SOURCE_TYPE]:  'Invalid source type — must be court-filed or client-held.',
  [ERROR_CODES.ORIGINAL_INVALID_CATEGORY]:     'Invalid document category.',
  [ERROR_CODES.ORIGINAL_ALREADY_CHECKED_OUT]:  'This original is already checked out.',
  [ERROR_CODES.ORIGINAL_NOT_CHECKED_OUT]:      'This original is not currently checked out.',
  [ERROR_CODES.ORIGINAL_STORAGE_LOCATION_REQUIRED]: 'Please provide a storage location.',

  [ERROR_CODES.CLIENT_NOT_FOUND]:        'Client not found.',
  [ERROR_CODES.CLIENT_DUPLICATE_EMAIL]:  'A client with this email already exists.',
  [ERROR_CODES.CLIENT_INVALID_TYPE]:     'Invalid client type. Must be Individual or Corporate.',
  [ERROR_CODES.CLIENT_PORTAL_FAILED]:    'Failed to send portal access link. Please try again.',
  [ERROR_CODES.CLIENT_ALREADY_APPROVED]: 'This lead has already been approved.',
  [ERROR_CODES.CLIENT_ALREADY_REJECTED]: 'This lead has already been rejected.',
  [ERROR_CODES.CLIENT_ALREADY_ARCHIVED]:  'This client is already archived.',
  [ERROR_CODES.CLIENT_UNARCHIVE_BLOCKED]: 'This client is not archived and cannot be restored.',
  [ERROR_CODES.CLIENT_ARCHIVE_BLOCKED]:   'Cannot archive this client — they have active cases. Close and archive all cases first.',

  [ERROR_CODES.LEAD_NOT_FOUND]:          'Lead not found.',
  [ERROR_CODES.LEAD_INVALID_STATUS]:     'This action is not allowed for the current lead status.',
  [ERROR_CODES.LEAD_CONVERT_FAILED]:     'Failed to approve the lead. Please try again.',

  [ERROR_CODES.INVOICE_NOT_FOUND]:       'Invoice not found.',
  [ERROR_CODES.INVOICE_ALREADY_PAID]:    'This invoice is fully paid and cannot be modified.',
  [ERROR_CODES.INVOICE_IMMUTABLE]:       'Invoices cannot be edited after creation.',
  [ERROR_CODES.INVOICE_INVALID_AMOUNT]:  'Invoice amount must be greater than zero.',
  [ERROR_CODES.INVOICE_ALREADY_VOIDED]:  'This invoice has already been voided.',
  [ERROR_CODES.INVOICE_OVERPAYMENT]:     'Payment amount exceeds the remaining invoice balance.',

  [ERROR_CODES.PAYMENT_NOT_FOUND]:       'Payment not found.',
  [ERROR_CODES.PAYMENT_IMMUTABLE]:       'Payments cannot be edited after recording.',
  [ERROR_CODES.PAYMENT_INVALID_AMOUNT]:  'Payment amount must be greater than zero.',
  [ERROR_CODES.PAYMENT_METHOD_INVALID]:  'Invalid payment method selected.',
  [ERROR_CODES.PAYMENT_CHEQUE_CLEARED]:  'This cheque has already been marked as cleared.',
  [ERROR_CODES.PAYMENT_CHEQUE_BOUNCED]:  'This cheque has already been marked as bounced.',
  [ERROR_CODES.PAYMENT_NO_INVOICE_LINK]: 'Please link this payment to an invoice.',

  [ERROR_CODES.USER_NOT_FOUND]:          'User not found.',
  [ERROR_CODES.USER_DUPLICATE_EMAIL]:    'A user with this email already exists.',
  [ERROR_CODES.USER_INVALID_ROLE]:       'Invalid role selected.',
  [ERROR_CODES.USER_DELETE_LAST_ADMIN]:  'Cannot remove the last Admin from this firm.',
  [ERROR_CODES.USER_CLAIMS_SYNC_FAILED]: 'User permissions could not be updated. Please try again.',
  [ERROR_CODES.USER_DEACTIVATED]:        'This user account has been deactivated.',

  [ERROR_CODES.CONFLICT_STALE_VERSION]:    'This record was updated by someone else. Refresh to see the latest version.',
  [ERROR_CODES.CONFLICT_IMMUTABLE_DOC]:    'This record cannot be modified.',
  [ERROR_CODES.CONFLICT_CONCURRENT_WRITE]: 'The server is busy right now. Wait a moment and try saving again — your changes are still here.',

  [ERROR_CODES.GEN_UNKNOWN]:             'Something went wrong. Please try again.',
  [ERROR_CODES.GEN_NETWORK]:             'No connection to the server. Check your internet and try again.',
  [ERROR_CODES.GEN_VALIDATION]:          'Please check the form for errors and try again.',
  [ERROR_CODES.GEN_NOT_IMPLEMENTED]:     'This feature is not yet available.',

  default: 'Something went wrong. Please try again.',
};
